import { useParams, Link, Navigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { articleDetails } from '../../mocks/article-details';
import { articles } from '../../mocks/articles';
import { sendSubscribeWebhook } from '../../utils/webhookUtils';
import JsonLd from '../../components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://workplaysave.com';

/** Derive SEO keywords from the article's category + title tokens. */
function generateArticleKeywords(title: string, category: string): string {
  const categoryKeywords: Record<string, string> = {
    'Gaming Tech': 'gaming gear, gaming reviews, pc gaming, gaming setup, gaming accessories, best gaming equipment',
    'Streaming Equipment': 'streaming equipment, streaming setup, streaming gear, content creation, streaming accessories, streaming reviews',
    'Office Productivity': 'office productivity, office setup, work from home, ergonomic office, productivity tools, desk setup',
  };
  const base = categoryKeywords[category] ?? 'tech reviews, buying guides, best tech gear';
  // Extract meaningful words from the title (4+ chars, not stop words)
  const stopWords = new Set(['best', 'under', 'with', 'from', 'that', 'this', 'your', 'have', 'will', 'what', 'when', 'where', 'which', 'their', 'there', 'about', 'more', 'most', 'into', 'over', 'also']);
  const titleWords = title
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length >= 4 && !stopWords.has(w))
    .slice(0, 4)
    .join(', ');
  return titleWords ? `${base}, ${titleWords}` : base;
}

const TRUST_BADGES = [
  { icon: 'ri-shield-check-fill', text: 'Verified Amazon availability' },
  { icon: 'ri-price-tag-3-fill', text: 'No inflated pricing or fake listings' },
  { icon: 'ri-calendar-check-fill', text: 'Updated for 2026' },
];

function TrustMicrocopy() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
      {TRUST_BADGES.map((b, i) => (
        <span key={i} className="flex items-center gap-1 text-xs text-gray-500">
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={`${b.icon} text-emerald-500 text-[10px]`}></i>
          </span>
          {b.text}
        </span>
      ))}
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [expandedProducts, setExpandedProducts] = useState<Record<number, boolean>>({});
  const [expandedFAQs, setExpandedFAQs] = useState<Record<number, boolean>>({});
  const [nlEmail, setNlEmail] = useState('');
  const [nlStatus, setNlStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const article = slug ? articleDetails[slug] : null;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Work Play Save`;
    }
    return () => {
      document.title = 'Work Play Save | Gaming, Streaming & Tech Reviews';
    };
  }, [article]);

  const articleSchema = useMemo(() => {
    if (!article || !slug) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.content.introduction[0] ?? '',
      image: article.featuredImage,
      datePublished: article.publishDate,
      dateModified: article.publishDate,
      author: {
        '@type': 'Organization',
        name: 'Work Play Save',
        url: SITE_URL,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Work Play Save',
        url: SITE_URL,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/article/${slug}`,
      },
    };
  }, [article, slug]);

  const faqSchema = useMemo(() => {
    if (!article || !article.content.faq || article.content.faq.length === 0) return null;

    // Deduplicate by question text to guarantee a single FAQPage block — no duplicates
    const seen = new Set<string>();
    const uniqueFaqs = article.content.faq.filter((faq) => {
      if (seen.has(faq.question)) return false;
      seen.add(faq.question);
      return true;
    });

    if (uniqueFaqs.length === 0) return null;

    // Single authoritative FAQPage schema — only ONE <JsonLd> below should ever render this
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: uniqueFaqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };
  }, [article]);

  const itemListSchema = useMemo(() => {
    if (!article || !slug) return null;
    const products = article.content.productRecommendations?.products ?? [];
    if (products.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: article.content.productRecommendations.title,
      url: `${SITE_URL}/article/${slug}`,
      numberOfItems: products.length,
      itemListElement: products.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: p.name,
        url: p.affiliateLink,
      })),
    };
  }, [article, slug]);

  if (!slug || !articleDetails[slug]) {
    return <Navigate to="/404" replace />;
  }

  const relatedArticlesData = article!.relatedArticles
    .map(relatedSlug => articles.find(a => a.slug === relatedSlug))
    .filter(Boolean)
    .slice(0, 3);

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'gaming tech': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'streaming equipment': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'office productivity': return 'bg-green-500/20 text-green-300 border-green-500/30';
      default: return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30';
    }
  };

  const getBadgeStyle = (badge: string): string => {
    switch (badge) {
      case "Editor's Choice": return 'bg-cyan-500 text-white';
      case 'Best Overall': return 'bg-cyan-500 text-white';
      case 'Best Value': return 'bg-emerald-500 text-white';
      case 'Best Budget': return 'bg-purple-500 text-white';
      case 'Best Premium': return 'bg-amber-400 text-gray-900';
      case 'Best Value 240Hz': return 'bg-rose-500 text-white';
      case 'Best High Refresh': return 'bg-orange-500 text-white';
      default: return 'bg-cyan-500 text-white';
    }
  };

  const getBadgeIcon = (badge: string): string => {
    switch (badge) {
      case "Editor's Choice": return 'ri-medal-fill';
      case 'Best Overall': return 'ri-trophy-fill';
      case 'Best Value': return 'ri-price-tag-3-fill';
      case 'Best Budget': return 'ri-wallet-3-fill';
      case 'Best Premium': return 'ri-vip-crown-fill';
      case 'Best Value 240Hz': return 'ri-speed-up-fill';
      case 'Best High Refresh': return 'ri-flashlight-fill';
      default: return 'ri-award-fill';
    }
  };

  const getBestForColor = (badge: string) => {
    switch (badge) {
      case "Editor's Choice": return 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30';
      case 'Best Overall': return 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30';
      case 'Best Value': return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
      case 'Best Budget': return 'bg-purple-500/15 text-purple-300 border-purple-500/30';
      case 'Best Premium': return 'bg-amber-400/15 text-amber-300 border-amber-400/30';
      case 'Best Value 240Hz': return 'bg-rose-500/15 text-rose-300 border-rose-500/30';
      case 'Best High Refresh': return 'bg-orange-500/15 text-orange-300 border-orange-500/30';
      default: return 'bg-gray-500/15 text-gray-300 border-gray-500/30';
    }
  };

  const toggleProduct = (index: number) => {
    setExpandedProducts(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleFAQ = (index: number) => {
    setExpandedFAQs(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nlEmail) return;
    setNlStatus('submitting');
    try {
      const [res] = await Promise.allSettled([
        fetch('https://readdy.ai/api/form/d6t4n6vcrcujsk0v13d0', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ email: nlEmail, source: 'Article Signup' }).toString(),
        }),
        sendSubscribeWebhook({
          email: nlEmail,
          source: 'Article Signup',
          page_url: window.location.href,
          article_title: article!.title,
        }),
      ]);

      const localOk = res.status === 'fulfilled' && (res.value as Response).ok;
      if (localOk) {
        setNlStatus('success');
        setNlEmail('');
      } else {
        setNlStatus('error');
      }
    } catch {
      setNlStatus('error');
    } finally {
      setTimeout(() => setNlStatus((s) => (s !== 'success' ? 'idle' : s)), 5000);
    }
  };

  return (
    <>
      <title>{article!.title} | Work Play Save</title>
      <meta name="description" content={article!.content.introduction[0] ?? article!.title} />
      <meta name="keywords" content={generateArticleKeywords(article!.title, article!.category)} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}/article/${slug}`} />
      <meta property="og:title" content={`${article!.title} | Work Play Save`} />
      <meta property="og:description" content={article!.content.introduction[0] ?? article!.title} />
      <meta property="og:url" content={`${SITE_URL}/article/${slug}`} />
      <meta property="og:image" content={article!.featuredImage} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Work Play Save" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article!.title} | Work Play Save`} />
      <meta name="twitter:description" content={article!.content.introduction[0] ?? article!.title} />
      <meta name="twitter:image" content={article!.featuredImage} />
      {articleSchema && <JsonLd data={articleSchema} />}
      {faqSchema && <JsonLd data={faqSchema} />}
      {itemListSchema && <JsonLd data={itemListSchema} />}
      <div className="min-h-screen">
        {/* Article Header */}
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="mb-6">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold border ${getCategoryColor(article!.category)}`}>
              {article!.category}
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {article!.title}
          </h1>

          <div className="flex items-center space-x-4 text-gray-400 mb-8">
            <span className="flex items-center space-x-2">
              <i className="ri-calendar-line"></i>
              <span>{article!.publishDate}</span>
            </span>
          </div>

          {/* Featured Image */}
          <div className="w-full h-96 rounded-2xl overflow-hidden mb-12">
            <img
              src={article!.featuredImage}
              alt={article!.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Introduction */}
          <div className="prose prose-invert prose-lg max-w-none mb-6">
            {article!.content.introduction.map((paragraph, index) => (
              <p key={index} className="text-gray-300 text-lg leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>

          {/* ── INTERNAL LINKS BAR ── */}
          {article!.internalLinks && article!.internalLinks.length > 0 && (
            <div className="mb-10 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 uppercase tracking-widest font-bold flex-shrink-0">Also see:</span>
              {article!.internalLinks.map((link, i) => (
                <Link
                  key={i}
                  to={`/article/${link.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-800/60 border border-gray-700 hover:border-cyan-500/50 text-sm text-gray-300 hover:text-cyan-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-right-line text-xs text-cyan-400"></i>
                  </span>
                  {link.anchorText}
                </Link>
              ))}
            </div>
          )}

          {/* ── QUICK PICKS (Skip the Research) ── */}
          <div className="mb-12 rounded-2xl border border-amber-500/30 bg-amber-500/5 overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-amber-500/20 bg-amber-500/10">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-flashlight-fill text-amber-400 text-base"></i>
              </span>
              <h2 className="text-lg font-bold text-white">
                Quick Picks{' '}
                <span className="text-gray-400 font-normal text-base">(Skip the Research)</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-amber-500/10">
              {article!.quickVerdict.picks.map((pick, i) => (
                <div key={i} className="p-5 flex flex-col gap-3">
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">{pick.label}</span>
                  <a
                    href={pick.affiliateLink}
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    className="text-sm font-bold text-white leading-snug hover:text-amber-300 transition-colors cursor-pointer"
                  >
                    {pick.productName}
                  </a>
                  <p className="text-gray-400 text-xs leading-relaxed flex-1">{pick.reason}</p>
                  <a
                    href={pick.affiliateLink}
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    className="flex-shrink-0 w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-emerald-500 text-white px-6 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all whitespace-nowrap cursor-pointer text-sm text-center"
                  >
                    See Latest Price <i className="ri-arrow-right-line ml-1"></i>
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Table of Contents — REMOVED */}

          {/* Best Overall CTA Banner */}
          {(() => {
            const bestOverall = article!.quickVerdict.picks.find(p => p.label === 'Best Overall');
            if (!bestOverall) return null;
            return (
              <div className="mb-12 flex flex-col sm:flex-row items-start sm:items-center gap-5 p-5 bg-cyan-500/10 rounded-2xl border border-cyan-500/30">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-trophy-line text-cyan-400 text-2xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-cyan-400 font-bold uppercase tracking-widest block mb-1">Editor&apos;s Top Pick</span>
                  <p className="text-white font-bold text-base leading-snug">{bestOverall.productName}</p>
                  <p className="text-gray-400 text-sm mt-0.5">{bestOverall.reason}</p>
                </div>
                <a
                  href={bestOverall.affiliateLink}
                  target="_blank"
                  rel="noopener noreferrer nofollow sponsored"
                  className="flex-shrink-0 w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-emerald-500 text-white px-6 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all whitespace-nowrap cursor-pointer text-sm text-center"
                >
                  See Latest Price <i className="ri-arrow-right-line ml-1"></i>
                </a>
              </div>
            );
          })()}

          {/* Quick Verdict */}
          <div id="quick-verdict" className="mb-8 bg-gradient-to-br from-cyan-500/10 via-purple-600/10 to-emerald-500/10 rounded-2xl p-8 border border-cyan-500/30">
            <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
              <i className="ri-award-line text-cyan-400"></i>
              <span>Quick Verdict</span>
            </h2>

            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              {article!.quickVerdict.summary}
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {article!.quickVerdict.picks.map((pick, index) => {
                const colors = [
                  { bg: 'bg-cyan-500/20', border: 'border-cyan-500/50', text: 'text-cyan-400', btn: 'from-cyan-500 to-cyan-600', glow: 'hover:shadow-cyan-500/30' },
                  { bg: 'bg-purple-500/20', border: 'border-purple-500/50', text: 'text-purple-400', btn: 'from-purple-500 to-purple-600', glow: 'hover:shadow-purple-500/30' },
                  { bg: 'bg-emerald-500/20', border: 'border-emerald-500/50', text: 'text-emerald-400', btn: 'from-emerald-500 to-emerald-600', glow: 'hover:shadow-emerald-500/30' },
                  { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400', btn: 'from-amber-500 to-amber-600', glow: 'hover:shadow-amber-500/30' },
                  { bg: 'bg-rose-500/20', border: 'border-rose-500/50', text: 'text-rose-400', btn: 'from-rose-500 to-rose-600', glow: 'hover:shadow-rose-500/30' },
                ];
                const color = colors[index % colors.length];

                return (
                  <div key={index} className={`${color.bg} border ${color.border} rounded-xl p-6 flex flex-col`}>
                    <div className={`text-sm font-bold ${color.text} mb-3 uppercase tracking-wide`}>
                      {pick.label}
                    </div>
                    <a
                      href={pick.affiliateLink}
                      target="_blank"
                      rel="noopener noreferrer nofollow sponsored"
                      className="text-xl font-bold text-white mb-3 leading-tight hover:underline cursor-pointer"
                    >
                      {pick.productName}
                    </a>
                    <p className="text-gray-300 text-sm leading-relaxed flex-1 mb-5">
                      {pick.reason}
                    </p>
                    <a
                      href={pick.affiliateLink}
                      target="_blank"
                      rel="noopener noreferrer nofollow sponsored"
                      className={`w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r ${color.btn} text-white px-4 py-2.5 rounded-lg text-xs font-bold hover:shadow-lg ${color.glow} transition-all whitespace-nowrap cursor-pointer`}
                    >
                      Check Price on Amazon <i className="ri-external-link-line"></i>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Comparison Table */}
          <div id="comparison-table" className="mb-6">
            <h2 className="text-4xl font-bold text-white mb-8 flex items-center space-x-3">
              <i className="ri-table-line text-purple-400"></i>
              <span>Quick Comparison</span>
            </h2>

            <div className="overflow-x-auto rounded-2xl border border-gray-700">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-gradient-to-r from-cyan-500/20 to-purple-600/20 border-b border-gray-700">
                    <th className="text-left p-4 text-cyan-400 font-bold text-sm uppercase tracking-wide">Product</th>
                    {article!.content.comparisonTableColumns
                      .filter(col => col.key !== 'price')
                      .map(col => (
                        <th key={col.key} className="text-left p-4 text-cyan-400 font-bold text-sm uppercase tracking-wide">
                          {col.label}
                        </th>
                      ))}
                    <th className="text-left p-4 text-cyan-400 font-bold text-sm uppercase tracking-wide">
                      {article!.hidePricing ? 'Amazon' : 'Price'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {article!.content.comparisonTable.map((row, index) => (
                    <tr key={index} className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors">
                      <td className="p-4">
                        <a
                          href={row.affiliateLink}
                          target="_blank"
                          rel="noopener noreferrer nofollow sponsored"
                          className="text-white font-semibold hover:text-cyan-400 transition-colors cursor-pointer"
                        >
                          {row.productName}
                        </a>
                      </td>
                      {article!.content.comparisonTableColumns
                        .filter(col => col.key !== 'price')
                        .map(col => {
                          const value = row[col.key as keyof typeof row];
                          return (
                            <td key={col.key} className="p-4 text-gray-300 text-sm">
                              {value ?? 'N/A'}
                            </td>
                          );
                        })}
                      {!article!.hidePricing && (
                        <td className="p-4 text-emerald-400 font-bold">{row.price}</td>
                      )}
                      {article!.hidePricing && (
                        <td className="p-4">
                          <a
                            href={row.affiliateLink}
                            target="_blank"
                            rel="noopener noreferrer nofollow sponsored"
                            className="inline-flex items-center gap-1.5 bg-cyan-500/15 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-300 px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer"
                          >
                            View Latest Price <i className="ri-external-link-line"></i>
                          </a>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {article!.hidePricing && (
              <p className="text-gray-500 text-sm mt-3 italic flex items-center gap-1.5">
                <i className="ri-information-line text-gray-400"></i>
                Prices may vary over time, so check the latest price on Amazon.
              </p>
            )}
          </div>

          {/* Product Recommendations */}
          <div id="product-picks" className="mb-16">
            <h2 className="text-4xl font-bold text-white mb-8">
              {article!.content.productRecommendations.title}
            </h2>

            <div className="space-y-8">
              {article!.content.productRecommendations.products.map((product, index) => (
                <div
                  key={index}
                  className="relative bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700 hover:border-cyan-500/50 transition-all overflow-hidden group"
                >
                  {/* Fully clickable overlay — sits behind all interactive elements */}
                  <a
                    href={product.affiliateLink}
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    className="absolute inset-0 z-0 rounded-2xl"
                    aria-label={`View ${product.name} on Amazon`}
                  ></a>

                  {/* Best For ribbon at top */}
                  {product.badge && (
                    <div className={`relative z-10 flex items-center gap-2 px-6 py-2.5 border-b border-gray-700/60 ${getBestForColor(product.badge)}`}>
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={`${getBadgeIcon(product.badge)} text-xs`}></i>
                      </span>
                      <span className="text-xs font-bold uppercase tracking-widest">{product.badge}</span>
                    </div>
                  )}

                  <div className="relative z-10 flex flex-col md:flex-row gap-8 p-8">
                    {/* Product Image */}
                    <div className="w-full md:w-56 flex-shrink-0">
                      <a
                        href={product.affiliateLink}
                        target="_blank"
                        rel="noopener noreferrer nofollow sponsored"
                        className="block cursor-pointer relative"
                      >
                        <div className="w-full aspect-square rounded-xl overflow-hidden bg-gray-700/50 border border-gray-600 group-hover:border-cyan-500/50 transition-colors">
                          <div className="w-full h-full p-4 flex items-center justify-center">
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        </div>
                        {product.badge && (
                          <span className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide leading-none shadow-lg ${getBadgeStyle(product.badge)}`}>
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className={`${getBadgeIcon(product.badge)} text-[10px]`}></i>
                            </span>
                            {product.badge}
                          </span>
                        )}
                      </a>
                      {!article!.hidePricing && (
                        <div className="mt-3 text-center">
                          <div className="text-3xl font-bold text-emerald-400" style={{ textShadow: '0 0 10px rgba(52,211,153,0.5)' }}>{product.price}</div>
                          <div className="text-xs text-gray-500 mt-0.5">Price at time of writing</div>
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col items-start mb-1 gap-2">
                        <a
                          href={product.affiliateLink}
                          target="_blank"
                          rel="noopener noreferrer nofollow sponsored"
                          className="text-2xl font-bold text-white leading-tight hover:text-cyan-400 transition-colors cursor-pointer"
                        >
                          {product.name}
                        </a>
                        <div className="flex items-center gap-1.5 bg-amber-500/15 border border-amber-500/40 rounded-full px-3 py-1 flex-shrink-0">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          <span className="text-amber-400 font-bold text-sm whitespace-nowrap">Editor Rating {product.editorRating}/10</span>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6 mb-6 mt-4">
                        <div>
                          <h4 className="text-green-400 font-semibold mb-3 flex items-center space-x-2">
                            <i className="ri-checkbox-circle-line"></i>
                            <span>Pros</span>
                          </h4>
                          <ul className="space-y-2">
                            {product.pros.map((pro, i) => (
                              <li key={i} className="text-gray-300 flex items-start space-x-2">
                                <span className="text-green-400 mt-1">•</span>
                                <span>{pro}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="text-red-400 font-semibold mb-3 flex items-center space-x-2">
                            <i className="ri-close-circle-line"></i>
                            <span>Cons</span>
                          </h4>
                          <ul className="space-y-2">
                            {product.cons.map((con, i) => (
                              <li key={i} className="text-gray-300 flex items-start space-x-2">
                                <span className="text-red-400 mt-1">•</span>
                                <span>{con}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Expandable Details */}
                      <div className="mb-6">
                        <button
                          onClick={() => toggleProduct(index)}
                          className="relative z-10 flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 font-semibold transition-colors cursor-pointer"
                        >
                          <span>{expandedProducts[index] ? 'Hide' : 'Show'} Detailed Review</span>
                          <i className={`ri-arrow-${expandedProducts[index] ? 'up' : 'down'}-s-line`}></i>
                        </button>

                        {expandedProducts[index] && (
                          <div className="mt-6 space-y-6 border-t border-gray-700 pt-6">
                            <div>
                              <h5 className="text-lg font-bold text-purple-400 mb-3 flex items-center space-x-2">
                                <i className="ri-hammer-line"></i>
                                <span>Build Quality</span>
                              </h5>
                              <p className="text-gray-300 leading-relaxed">{product.buildQuality}</p>
                            </div>

                            <div>
                              <h5 className="text-lg font-bold text-cyan-400 mb-3 flex items-center space-x-2">
                                <i className="ri-speed-line"></i>
                                <span>Performance</span>
                              </h5>
                              <p className="text-gray-300 leading-relaxed">{product.performance}</p>
                            </div>

                            {product.gamingExperience && (
                              <div>
                                <h5 className="text-lg font-bold text-emerald-400 mb-3 flex items-center space-x-2">
                                  <i className="ri-gamepad-line"></i>
                                  <span>Gaming Experience</span>
                                </h5>
                                <p className="text-gray-300 leading-relaxed">{product.gamingExperience}</p>
                              </div>
                            )}

                            <div>
                              <h5 className="text-lg font-bold text-yellow-400 mb-3 flex items-center space-x-2">
                                <i className="ri-money-dollar-circle-line"></i>
                                <span>Value for Money</span>
                              </h5>
                              <p className="text-gray-300 leading-relaxed">{product.valueForMoney}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ── DUAL CTA BUTTONS ── */}
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                          {/* Primary CTA */}
                          <a
                            href={product.affiliateLink}
                            target="_blank"
                            rel="noopener noreferrer nofollow sponsored"
                            className="relative z-10 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white px-8 py-3 rounded-full font-bold hover:shadow-lg hover:shadow-cyan-500/50 transition-all whitespace-nowrap cursor-pointer text-sm"
                          >
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-amazon-fill text-base"></i>
                            </span>
                            Check Price on Amazon
                          </a>
                          {/* Secondary CTA */}
                          <button
                            onClick={() => toggleProduct(index)}
                            className="relative z-10 w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-gray-600 hover:border-cyan-500/60 text-gray-300 hover:text-white px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer text-sm"
                          >
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-file-text-line text-sm"></i>
                            </span>
                            View Full Review
                          </button>
                        </div>
                        <TrustMicrocopy />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison */}
          <div className="mb-16 bg-gradient-to-br from-cyan-500/10 to-purple-600/10 rounded-2xl p-8 border border-cyan-500/30">
            <h2 className="text-3xl font-bold text-white mb-4">
              {article!.content.comparison.title}
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              {article!.content.comparison.description}
            </p>
          </div>

          {/* Buying Guide */}
          <div id="buying-guide" className="mb-16">
            <h2 className="text-4xl font-bold text-white mb-8">
              {article!.content.buyingGuide.title}
            </h2>

            <div className="space-y-6">
              {article!.content.buyingGuide.tips.map((tip, index) => {
                const [title, ...description] = tip.split(':');
                return (
                  <div key={index}>
                    <h3 className="text-xl font-bold text-cyan-400 mb-2">{title}</h3>
                    <p className="text-gray-300 leading-relaxed">{description.join(':')}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* FAQ Section */}
          <div id="faq" className="mb-16">
            <h2 className="text-4xl font-bold text-white mb-8 flex items-center space-x-3">
              <i className="ri-question-answer-line text-emerald-400"></i>
              <span>Frequently Asked Questions</span>
            </h2>

            <div className="space-y-4">
              {article!.content.faq.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700 overflow-hidden hover:border-cyan-500/50 transition-all"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full p-6 flex items-start justify-between space-x-4 text-left cursor-pointer"
                  >
                    <div className="flex items-start space-x-4 flex-1">
                      <i className="ri-question-line text-cyan-400 text-xl mt-1 flex-shrink-0"></i>
                      <h3 className="text-lg font-bold text-white leading-tight">
                        {faq.question}
                      </h3>
                    </div>
                    <i className={`ri-arrow-${expandedFAQs[index] ? 'up' : 'down'}-s-line text-gray-400 text-xl flex-shrink-0 mt-1`}></i>
                  </button>

                  {expandedFAQs[index] && (
                    <div className="px-6 pb-6 border-t border-gray-700 pt-4">
                      <p className="text-gray-300 leading-relaxed pl-9">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Conclusion */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-white mb-8">Conclusion</h2>
            <div className="prose prose-invert prose-lg max-w-none">
              {article!.content.conclusion.map((paragraph, index) => (
                <p key={index} className="text-gray-300 text-lg leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticlesData.length > 0 && (
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-white mb-8">Related Articles</h2>

              <div className="grid md:grid-cols-3 gap-6">
                {relatedArticlesData.map((relatedArticle) => (
                  <Link
                    key={relatedArticle?.slug}
                    to={`/article/${relatedArticle?.slug}`}
                    className="group bg-gray-800/30 backdrop-blur-sm rounded-xl overflow-hidden border border-gray-700 hover:border-cyan-500/50 transition-all"
                  >
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={relatedArticle?.image}
                        alt={relatedArticle?.title}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>

                    <div className="p-6">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border mb-3 ${getCategoryColor(relatedArticle?.category || '')}`}>
                        {relatedArticle?.category}
                      </span>

                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                        {relatedArticle?.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Article-page Newsletter Signup */}
          <div id="article-newsletter-form" className="mb-16">
            <div className="rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 via-purple-600/5 to-emerald-500/5 overflow-hidden">
              <div className="h-px w-full bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
              <div className="px-8 py-10 flex flex-col lg:flex-row items-start lg:items-center gap-8">
                {/* Left copy */}
                <div className="flex-1 min-w-0">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-cyan-500/25 rounded-full text-xs uppercase tracking-widest text-cyan-400 bg-cyan-500/5 mb-4">
                    <i className="ri-mail-line text-cyan-400"></i>
                    Stay in the Loop
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Get the Best Tech Picks in Your Inbox
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    New reviews, buying guides, and deal alerts delivered weekly. No spam, unsubscribe anytime.
                  </p>
                </div>

                {/* Right form */}
                <div className="w-full lg:w-80 flex-shrink-0">
                  {nlStatus === 'success' ? (
                    <div className="flex items-start gap-3 px-5 py-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-emerald-400 text-lg"></i>
                      </div>
                      <p className="text-emerald-300 text-sm font-medium leading-snug">
                        Thanks for subscribing. Check your email.
                      </p>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleNewsletterSubmit}
                      className="flex flex-col gap-3"
                    >
                      <input type="hidden" name="source" value="article-page" />
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={nlEmail}
                          onChange={(e) => setNlEmail(e.target.value)}
                          placeholder="Enter your email"
                          required
                          className="w-full px-5 py-3.5 bg-black/40 border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 focus:shadow-[0_0_0_3px_rgba(6,182,212,0.08)] transition-all duration-200 text-sm pr-14"
                        />
                        <button
                          type="submit"
                          disabled={nlStatus === 'submitting'}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-lg text-white hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-200 disabled:opacity-50 cursor-pointer"
                        >
                          {nlStatus === 'submitting' ? (
                            <i className="ri-loader-4-line animate-spin text-sm"></i>
                          ) : (
                            <i className="ri-send-plane-fill text-sm"></i>
                          )}
                        </button>
                      </div>
                      {nlStatus === 'error' && (
                        <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
                      )}
                      <p className="text-xs text-gray-600">
                        No spam. Unsubscribe anytime. We respect your privacy.
                      </p>
                    </form>
                  )}
                </div>
              </div>
              <div className="h-px w-full bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
